/* eslint-disable @typescript-eslint/no-explicit-any */
import { Edit as EditIcon } from '@mui/icons-material';
import { Button, Grid, IconButton, Stack, Switch, TextField } from '@mui/material';
import React from 'react';
import AdminLayoutView from 'src/Layout/AdminLayout/AdminLayoutView';
import AdminTabs from 'src/Layout/AdminLayout/AdminTabs';
import { AdminTabValue } from 'src/Layout/AdminLayout/AdminTabs/types';
import AdminViewWrapper from 'src/Layout/AdminLayout/AdminViewWrapper';
import { BhiDialog } from 'src/UIKit';
import GridData from 'src/components/GridData/GridData';
import { TableHeader, TABLE_HEADER_ACTION_SX } from 'src/components/TableHeader/TableHeader';
import useConfirmationDialog from 'src/components/ConfirmationDialog/view';
import { useAdminConfigCategoriesViewController } from '../../controllers';
import { CellText } from '../CellText';
import { IconField } from 'src/modules/campaignConfigBuilder/components/fields/IconField';

export const AdminConfigCategoriesView = () => {
  const {
    isPageLoading,
    configCategories,
    filters,
    totalCount,
    changeSearchFilter,
    changeSorting,
    changePagination,
    isDialogOpen,
    dialogMode,
    formData,
    formErrors,
    isSubmitting,
    openCreateDialog,
    closeDialog,
    openEditDialog,
    handleCreate,
    handleUpdate,
    handleActivate,
    handleDelete,
    updateFormField,
  } = useAdminConfigCategoriesViewController();

  const { openDialog, ConfirmationDialog } = useConfirmationDialog();

  const handleToggleClick = (category: any) => {
    const isDeleted = category.status === 'deleted';
    openDialog({
      title: isDeleted ? 'Activate Config Category' : 'Deactivate Config Category',
      content: `Are you sure you want to ${isDeleted ? 'activate' : 'deactivate'} the config category "${category.title}"?`,
      onConfirm: () => isDeleted ? handleActivate(category) : handleDelete([category.name]),
      confirmButtonText: isDeleted ? 'Activate' : 'Deactivate',
      confirmButtonColor: isDeleted ? 'primary' : 'error',
      cancelButtonVariant: 'outlined',
    });
  };

  const isFormInvalid = () => {
    return (
      !formData.name || !formData.title || !formData.icon || !formData.rank
    );
  };

  return (
    <>
      <ConfirmationDialog />

      <AdminViewWrapper>
        <AdminLayoutView tabs={<AdminTabs value={AdminTabValue.configCategories} />}>
          <GridData
            header={
              <TableHeader
                searchValue={filters.query || undefined}
                onSearchChange={changeSearchFilter}
                searchPlaceholder="Search categories"
                actions={
                  <Button
                    onClick={openCreateDialog}
                    variant="contained"
                    sx={TABLE_HEADER_ACTION_SX}
                  >
                    New Category
                  </Button>
                }
              />
            }
            isLoading={isPageLoading}
            rows={configCategories}
            getRowId={row => row.name}
            columns={[
              {
                field: 'title',
                headerName: 'Title',
                editable: false,
                sortable: false,
                flex: 1,
                renderCell: params => (
                  <CellText muted={params.row.status === 'deleted'}>
                    {params.row.title}
                  </CellText>
                ),
              },
              {
                field: 'name',
                headerName: 'Name',
                editable: false,
                sortable: false,
                flex: 1,
                renderCell: params => (
                  <CellText muted={params.row.status === 'deleted'}>
                    {params.row.name}
                  </CellText>
                ),
              },
              {
                field: 'description',
                headerName: 'Description',
                editable: false,
                sortable: false,
                flex: 3,
                renderCell: params => (
                  <CellText muted={params.row.status === 'deleted'}>
                    {params.row.description || '-'}
                  </CellText>
                ),
              },
              {
                field: 'rank',
                headerName: 'Rank',
                editable: false,
                sortable: false,
                flex: 1,
                renderCell: params => (
                  <CellText muted={params.row.status === 'deleted'}>
                    {params.row.rank}
                  </CellText>
                ),
              },
              {
                field: 'status',
                headerName: 'Status',
                editable: false,
                sortable: false,
                flex: 1,
                renderCell: params => (
                  <CellText muted={params.row.status === 'deleted'}>
                    {params.row.status === 'deleted' ? 'Deactivated' : 'Active'}
                  </CellText>
                ),
              },
            ]}
            renderAction={row => (
              <>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => openEditDialog(row)}
                >
                  <EditIcon fontSize="inherit" />
                </IconButton>
                <Switch
                  size="small"
                  checked={row.status !== 'deleted'}
                  onChange={() => handleToggleClick(row)}
                />
              </>
            )}
            sorting={{
              value: filters.sort,
              onChange: changeSorting,
            }}
            pagination={{
              page: Math.floor((filters.offset || 0) / (filters.limit || 25)),
              pageSize: filters.limit || 25,
              totalCount: totalCount,
              onChange: changePagination,
            }}
          />
        </AdminLayoutView>
      </AdminViewWrapper>

      {/* Config Category Dialog */}
      <BhiDialog
        isOpen={isDialogOpen}
        onClose={closeDialog}
        title={dialogMode === 'create' ? 'Create Config Category' : 'Edit Config Category'}
        maxWidth="md"
      >
        <Grid container spacing={2} mt={1} p={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Name"
              value={formData.name}
              onChange={e => updateFormField('name', e.target.value)}
              error={!!formErrors.name}
              helperText={formErrors.name}
              disabled={dialogMode === 'edit'}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Title"
              value={formData.title}
              onChange={e => updateFormField('title', e.target.value)}
              error={!!formErrors.title}
              helperText={formErrors.title}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={e => updateFormField('description', e.target.value)}
              error={!!formErrors.description}
              helperText={formErrors.description}
              multiline
              rows={3}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth
              label="Rank"
              type="number"
              inputProps={{ min: 1 }}
              value={formData.rank}
              onChange={e => updateFormField('rank', parseInt(e.target.value) || 1)}
              error={!!formErrors.rank}
              helperText={formErrors.rank}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <IconField
              value={formData.icon}
              onChange={value => updateFormField('icon', value)}
              isSelected={false}
              onClick={() => {}}
            />
          </Grid>
         
          <Grid item xs={12}>
            <Stack direction="row" justifyContent="flex-end" gap={2} mt={2}>
              <Button variant="outlined" onClick={closeDialog} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                onClick={dialogMode === 'create' ? handleCreate : handleUpdate}
                variant="contained"
                disabled={isSubmitting || isFormInvalid()}
              >
                {isSubmitting
                  ? dialogMode === 'create'
                    ? 'Creating...'
                    : 'Updating...'
                  : dialogMode === 'create'
                  ? 'Create'
                  : 'Update'}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </BhiDialog>
    </>
  );
};
